/**
 * Meta WhatsApp API Integration Utilities
 * Handles sending messages, templates, and broadcasts via Meta WhatsApp Business API
 */

import { API_POST } from '../../services/api.service';

/**
 * Send a single message via Meta WhatsApp API
 * @param {Object} messageData - Message data including phone, template, variables
 * @param {Object} user - User information
 * @returns {Promise<Object>} API response
 */
export const sendWhatsAppMessage = async (messageData, user) => {
  try {
    const response = await API_POST('whatsapp/sendMessage', {
      ...messageData,
      admin: user.admin,
      username: user.username
    });
    
    return response;
  } catch (error) {
    console.error('Error sending WhatsApp message:', error);
    throw error;
  }
};

/**
 * Send a broadcast message to multiple recipients
 * @param {Object} broadcastData - Broadcast data including template, phone numbers, variables
 * @param {Object} user - User information
 * @returns {Promise<Object>} API response
 */
export const sendWhatsAppBroadcast = async (broadcastData, user) => {
  try {
    const response = await API_POST('whatsapp/sendBroadcast', {
      ...broadcastData,
      admin: user.admin,
      username: user.username
    });
    
    return response;
  } catch (error) {
    console.error('Error sending WhatsApp broadcast:', error);
    throw error;
  }
};

/**
 * Get template details from Meta API
 * @param {string} templateId - Template ID
 * @param {Object} user - User information
 * @returns {Promise<Object>} Template details
 */
export const getTemplateDetails = async (templateId, user) => {
  try {
    const response = await API_POST('whatsapp/getTemplate', {
      templateId,
      admin: user.admin,
      username: user.username
    });
    
    return response;
  } catch (error) {
    console.error('Error getting template details:', error);
    throw error;
  }
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {Object} Validation result
 */
export const validatePhoneNumber = (phone) => {
  // Remove all non-digit characters except +
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  
  // Check if it's a valid Indian number
  if (cleanPhone.startsWith('+91') && cleanPhone.length === 13) {
    return { valid: true, normalized: cleanPhone.substring(3) };
  }
  
  if (cleanPhone.startsWith('91') && cleanPhone.length === 12) {
    return { valid: true, normalized: cleanPhone.substring(2) };
  }
  
  if (cleanPhone.startsWith('0') && cleanPhone.length === 11) {
    return { valid: true, normalized: cleanPhone.substring(1) };
  }
  
  if (cleanPhone.length === 10 && /^\d{10}$/.test(cleanPhone)) {
    return { valid: true, normalized: cleanPhone };
  }
  
  return { valid: false, normalized: cleanPhone };
};

/**
 * Process template variables
 * @param {string} templateText - Template text with variables
 * @param {Object} variables - Variable values
 * @returns {string} Processed text
 */
export const processTemplateVariables = (templateText, variables) => {
  let processedText = templateText;
  
  Object.keys(variables).forEach(variable => {
    const regex = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
    processedText = processedText.replace(regex, variables[variable] || `{{${variable}}}`);
  });
  
  return processedText;
};

/**
 * Extract variables from template text
 * @param {string} templateText - Template text
 * @returns {Array} Array of variable names
 */
export const extractTemplateVariables = (templateText) => {
  const regex = /\{\{(\w+)\}\}/g;
  const variables = [];
  let match;
  
  while ((match = regex.exec(templateText)) !== null) {
    if (!variables.includes(match[1])) {
      variables.push(match[1]);
    }
  }
  
  return variables;
};

/**
 * Format phone number for WhatsApp API
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhoneForAPI = (phone) => {
  const validation = validatePhoneNumber(phone);
  
  if (!validation.valid) {
    throw new Error(`Invalid phone number format: ${phone}`);
  }
  
  // Return with country code for API
  return `91${validation.normalized}`;
};

/**
 * Batch process phone numbers for broadcast
 * @param {Array} phoneNumbers - Array of phone numbers
 * @returns {Object} Processed phone numbers with validation results
 */
export const processPhoneNumbersForBroadcast = (phoneNumbers) => {
  const validNumbers = [];
  const invalidNumbers = [];
  
  phoneNumbers.forEach(phone => {
    const validation = validatePhoneNumber(phone);
    if (validation.valid) {
      validNumbers.push({
        original: phone,
        normalized: validation.normalized,
        formatted: formatPhoneForAPI(phone)
      });
    } else {
      invalidNumbers.push({
        original: phone,
        error: 'Invalid format'
      });
    }
  });
  
  return {
    valid: validNumbers,
    invalid: invalidNumbers,
    total: phoneNumbers.length,
    validCount: validNumbers.length,
    invalidCount: invalidNumbers.length
  };
};

/**
 * Create broadcast payload for API
 * @param {Object} broadcastData - Broadcast form data
 * @param {Object} templateData - Selected template data
 * @param {Object} user - User information
 * @returns {Object} Formatted payload for API
 */
export const createBroadcastPayload = (broadcastData, templateData, user) => {
  const phoneNumbers = broadcastData.phoneNumbers.split('\n')
    .map(phone => phone.trim())
    .filter(phone => phone.length > 0);
  
  const processedPhones = processPhoneNumbersForBroadcast(phoneNumbers);
  
  return {
    broadcastName: broadcastData.broadcastName,
    templateId: broadcastData.selectedTemplate,
    templateName: templateData.name,
    phoneNumbers: processedPhones.valid,
    variables: broadcastData.variables,
    selectedPhone: broadcastData.selectedPhone,
    admin: user.admin,
    createdBy: user.username,
    createdOn: new Date(),
    status: 'pending',
    totalRecipients: processedPhones.validCount,
    invalidNumbers: processedPhones.invalid,
    isScheduled: broadcastData.isScheduled,
    scheduledTime: broadcastData.scheduledTime
  };
};

/**
 * Get broadcast logs by broadcastId
 * @param {string} broadcastId - The broadcast ID
 * @param {string} admin - Admin identifier
 * @returns {Promise} API response with broadcast logs
 */
export const getBroadcastLogs = async (broadcastId, admin) => {
  try {
    const response = await API_POST('whatsapp/getBroadcastLogs', {
      broadcastId,
      admin
    });
    return response;
  } catch (error) {
    console.error('Error fetching broadcast logs:', error);
    throw error;
  }
};